// texe-web/src/realtime/RealtimeBus.ts
// Basit, tip güvenli bir event bus. Uygulama genelinde tek örnek.

export type FeedNewPost = { postId: string; authorId: string };
export type FeedDeletePost = { postId: string };
export type CommentNew = { postId: string; comment: any };
export type CommentEdit = { postId: string; comment: any };
export type CommentDelete = { postId: string; commentId: string };
export type NotificationEvt = { userId?: string; payload: any };

type Handlers<T> = Set<(payload: T) => void>;

class RealtimeBus {
  // Genel kanallar
  private feedNewPostHandlers: Handlers<FeedNewPost> = new Set();
  private feedDeletePostHandlers: Handlers<FeedDeletePost> = new Set();
  private notificationHandlers: Handlers<NotificationEvt> = new Set();
  private pingHandlers: Handlers<number | undefined> = new Set();

  // Post’a özel comment kanalları (postId -> handler set)
  private commentNewMap = new Map<string, Handlers<CommentNew>>();
  private commentEditMap = new Map<string, Handlers<CommentEdit>>();
  private commentDeleteMap = new Map<string, Handlers<CommentDelete>>();

  // ---- Subscribe API ----
  onFeedNewPost(fn: (p: FeedNewPost) => void) {
    this.feedNewPostHandlers.add(fn);
    return () => this.feedNewPostHandlers.delete(fn);
  }
  onFeedDeletePost(fn: (p: FeedDeletePost) => void) {
    this.feedDeletePostHandlers.add(fn);
    return () => this.feedDeletePostHandlers.delete(fn);
  }
  onNotification(fn: (p: NotificationEvt) => void) {
    this.notificationHandlers.add(fn);
    return () => this.notificationHandlers.delete(fn);
  }
  onPing(fn: (t?: number) => void) {
    this.pingHandlers.add(fn);
    return () => this.pingHandlers.delete(fn);
  }

  // Yorumlar - postId bazlı
  onCommentNew(postId: string, fn: (p: CommentNew) => void) {
    const set = this.ensure(this.commentNewMap, postId);
    set.add(fn);
    return () => this.removeHandler(this.commentNewMap, postId, fn);
  }
  onCommentEdit(postId: string, fn: (p: CommentEdit) => void) {
    const set = this.ensure(this.commentEditMap, postId);
    set.add(fn);
    return () => this.removeHandler(this.commentEditMap, postId, fn);
  }
  onCommentDelete(postId: string, fn: (p: CommentDelete) => void) {
    const set = this.ensure(this.commentDeleteMap, postId);
    set.add(fn);
    return () => this.removeHandler(this.commentDeleteMap, postId, fn);
  }

  // ---- Emit (Provider kullanır) ----
  emitFeedNewPost(p: FeedNewPost) {
    this.dispatch(this.feedNewPostHandlers, p);
  }
  emitFeedDeletePost(p: FeedDeletePost) {
    this.dispatch(this.feedDeletePostHandlers, p);
  }
  emitNotification(p: NotificationEvt) {
    this.dispatch(this.notificationHandlers, p);
  }
  emitPing(t?: number) {
    this.dispatch(this.pingHandlers, t);
  }

  emitCommentNew(p: CommentNew) {
    this.forEachHandler(this.commentNewMap, p.postId, p);
  }
  emitCommentEdit(p: CommentEdit) {
    this.forEachHandler(this.commentEditMap, p.postId, p);
  }
  emitCommentDelete(p: CommentDelete) {
    this.forEachHandler(this.commentDeleteMap, p.postId, p);
  }

  // ---- helpers ----
  private ensure<T>(map: Map<string, Set<T>>, key: string): Set<T> {
    let set = map.get(key);
    if (!set) {
      set = new Set<T>();
      map.set(key, set);
    }
    return set;
  }
  private removeHandler<T>(map: Map<string, Set<T>>, key: string, fn: T) {
    const set = map.get(key);
    if (!set) return;
    set.delete(fn);
    if (set.size === 0) map.delete(key);
  }
  private dispatch<T>(set: Handlers<T>, payload: T) {
    set.forEach((fn) => {
      try {
        fn(payload);
      } catch {
        // tek handler hatası diğerlerini etkilemesin
      }
    });
  }
  private forEachHandler<T>(map: Map<string, Handlers<T>>, key: string, payload: T) {
    const set = map.get(key);
    if (!set) return;
    this.dispatch(set, payload);
  }
}

// Tekil instance
export const realtimeBus = new RealtimeBus();
